// FieldIQ — Tab registry — icon map, vertical fallbacks, per-facility resolution

import {
  MessageSquare,
  Users,
  MapPin,
  Search,
  Calendar,
  Activity,
  FileText,
  BookOpen,
  Navigation,
  Bell,
  ClipboardList,
  DollarSign,
  Truck,
  Package,
} from "lucide-react";

// Icon string → Lucide component map (mirrors verticals.tabs icon values in DB)
const TAB_ICON_MAP = {
  calendar: Calendar,
  "message-square": MessageSquare,
  users: Users,
  activity: Activity,
  book: BookOpen,
  "map-pin": MapPin,
  route: Navigation,
  clipboard: ClipboardList,
  dollar: DollarSign,
  map: MapPin,
  truck: Truck,
  chat: MessageSquare,
  search: Search,
  list: FileText,
  bell: Bell,
  package: Package,
};

// Static fallback — used when facility.resolved_tabs is absent (e.g. offline cache)
const VERTICAL_TABS = {
  political: [
    { id: "today", label: "Today", icon: Calendar },
    { id: "chat", label: "Lidy", icon: MessageSquare },
    { id: "people", label: "People", icon: Users },
    { id: "activities", label: "Activity", icon: Activity },
    { id: "topics", label: "Topics", icon: BookOpen },
    { id: "lookup", label: "Lookup", icon: MapPin },
  ],
  construction: [
    { id: "today", label: "Today", icon: Calendar },
    { id: "job_routes", label: "Routes", icon: Navigation },
    { id: "work_log", label: "Work Log", icon: ClipboardList },
    { id: "bid", label: "Bid", icon: DollarSign },
    { id: "site_lookup", label: "Sites", icon: MapPin },
    { id: "equipment", label: "Equipment", icon: Package },
    { id: "bob", label: "Ask Bob", icon: MessageSquare },
    { id: "materials", label: "Materials", icon: Truck },
  ],
};

// verticals.tabs carries two kinds of entry. Anything without `kind` is a nav
// tab. `kind: "feature"` is a switch for something *inside* a tab — it shares
// the array so it shares the admin editor and facilities.enabled_tabs, but it
// must never reach the tab bar.
const isFeature = (t) => t?.kind === "feature";

export function getTabsForFacility(facility) {
  // Prefer DB-resolved tabs returned by /my-services
  if (facility?.resolved_tabs?.length) {
    return facility.resolved_tabs
      .filter((t) => !isFeature(t))
      .map((t) => ({
        ...t,
        icon: TAB_ICON_MAP[t.icon] || Activity,
      }));
  }
  // Fallback: static map (offline cache or legacy response without resolved_tabs)
  const vertical = facility?.vertical_id || "political";
  const base = VERTICAL_TABS[vertical] || VERTICAL_TABS.political;
  const enabled = facility?.enabled_tabs;
  if (!enabled || !enabled.length)
    return base.filter((t) => t.id !== "materials");
  return base.filter((t) => enabled.includes(t.id));
}

/**
 * Is an in-tab feature switched on for this facility?
 *
 * Defaults to TRUE when the feature is unknown — an older cached /my-services
 * response, or the offline static fallback, has no entry for it, and hiding a
 * working feature because the payload predates it is the worse failure. It
 * only reads as off once the server has said so by omitting it from a
 * resolved_tabs list that carries other features.
 */
export function isFeatureEnabled(facility, featureId) {
  const features = facility?.resolved_features;
  // The KEY's presence is the signal, not its contents. Inferring state from
  // resolved_tabs could not work: a feature switched off is absent from that
  // list, which is the same shape as a payload from before features existed —
  // so "off" read as "unknown" and the toggle never took effect.
  if (Array.isArray(features)) {
    const match = features.find((f) => f.id === featureId);
    // An id this build knows about but the server does not send is new to the
    // server, not disabled by it — leave it on.
    return match ? match.enabled !== false : true;
  }
  return true; // older server, or offline cache: fail open
}
